import { useEffect, useMemo, useRef, useState } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddresses } from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { useRouter } from '@eneco/next';

import useBusLabel from './useBusLabel';
import { addressFormatter } from '../pages/my-eneco/move/confirmation-overview/address-formatter';
import { ADDRESS_PARAM } from '../utils/addSearchParams';
import {
  normalizeAddressSearchParam,
  resolveDeliveryAddressIdentifier,
  setSelectedDeliveryAddressId,
} from '../utils/selectedDeliveryAddress';

export const useAddressOptions = (config?: { suspense?: boolean }) => {
  const suspense = config?.suspense ?? false;
  const [addressIdentifier, setAddressIdentifier] = useState<string>('');
  const pendingSelectionRef = useRef<string | null>(null);
  const {
    selectedAccount: { crmAccountNumber: accountNumber },
  } = useSelfServiceAccount();
  const { data: session } = useSession();
  const { push, activePath, query } = useRouter();
  const rawQueryAddress = query[ADDRESS_PARAM];
  const queryAddressParam = Array.isArray(rawQueryAddress) ? rawQueryAddress[0] : rawQueryAddress;

  const {
    data,
    isLoading: isLoadingAddressList,
    error,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddresses,
    {
      path: {
        accountNumber,
      },
    },
    [`/accounts/${accountNumber}/delivery-addresses`],
    session,
    { suspense },
  );

  const busLabel = useBusLabel();

  const addressData = unwrapData(data);

  const addressOptions = useMemo(
    () =>
      addressData?.addresses
        ?.map(({ id, details, hasActiveContracts }) => ({
          value: id || '',
          label: addressFormatter(details, busLabel),
          hasActiveContracts,
        }))
        .sort((a, b) => (b.hasActiveContracts ? 1 : 0) - (a.hasActiveContracts ? 1 : 0)),
    [addressData, busLabel],
  );

  useEffect(() => {
    if (!addressOptions?.length || !accountNumber) return;

    const addressOptionValues = addressOptions.map(option => option.value).filter(Boolean);
    const searchParamAddress = normalizeAddressSearchParam(queryAddressParam ?? null);

    if (pendingSelectionRef.current !== null) {
      if (searchParamAddress === pendingSelectionRef.current) {
        pendingSelectionRef.current = null;
      } else {
        // Ignore router query updates until navigation catches up
        return;
      }
    }

    const resolved = resolveDeliveryAddressIdentifier({
      accountNumber,
      addressOptionValues,
      searchParamAddress,
    });

    setAddressIdentifier(resolved);

    if (searchParamAddress && resolved) {
      setSelectedDeliveryAddressId(accountNumber, resolved);
    }
  }, [accountNumber, addressOptions, queryAddressParam]);

  const setAddressIdentifierAndPush = (value: string) => {
    pendingSelectionRef.current = value;
    if (accountNumber) {
      setSelectedDeliveryAddressId(accountNumber, value);
    }
    setAddressIdentifier(value);
    push({ pathname: activePath, query: { ...query, address: value } }, undefined, { shallow: true });
  };

  const currentAddress = useMemo(() => {
    return addressData?.addresses?.find(address => address.id === addressIdentifier);
  }, [addressData, addressIdentifier]);

  const isWaitingForAddressIdentifier = useMemo(
    () => !addressIdentifier?.trim() && (addressOptions?.length ?? 0) > 0,
    [addressIdentifier, addressOptions],
  );

  const isLoading = useMemo(
    () => isLoadingAddressList || isWaitingForAddressIdentifier,
    [isLoadingAddressList, isWaitingForAddressIdentifier],
  );

  return {
    addressIdentifier,
    setAddressIdentifier: setAddressIdentifierAndPush,
    addressOptions,
    currentAddress,
    isLoading,
    error,
  };
};
